/**
 * FeaturedProductsStrip — horizontal auto-scrolling marquee of ⭐ featured
 * Amazon products, rendered as small polaroid cards. Placed on the H0 home
 * between the "De compres pel Món" pill and the Golf Tours section.
 *
 * If there are no featured products, renders nothing (silent).
 */
import React, { useEffect, useState, useMemo } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const COPY = {
  ca: { title: 'Destacats de la Botiga', cta: 'Veure tota la botiga →' },
  es: { title: 'Destacados de la Tienda', cta: 'Ver toda la tienda →' },
  en: { title: 'Shop Highlights', cta: 'See the whole shop →' },
  fr: { title: 'Sélection de la Boutique', cta: 'Voir toute la boutique →' },
};

const pickCopy = (lang) => COPY[(lang || 'ca').split('-')[0]] || COPY.ca;

// Slight, natural-looking rotations for the polaroid effect
const ROTATIONS = ['-2.5deg', '1.8deg', '-1.2deg', '2.4deg', '-1.6deg', '2deg', '-2.1deg', '1.4deg'];

const Card = ({ p, tilt }) => (
  <a
    href={p.amazon_url}
    target="_blank"
    rel="noopener noreferrer nofollow sponsored"
    data-testid={`featured-card-${p.id}`}
    className="group shrink-0 w-[180px] md:w-[210px] bg-[#F2E8D5] rounded-md shadow-lg hover:shadow-2xl transition-all duration-500"
    style={{ transform: `rotate(${tilt})` }}
  >
    <div className="p-2.5 md:p-3">
      <div className="aspect-square bg-white overflow-hidden rounded-sm relative">
        <img
          src={p.image_url}
          alt={p.title}
          loading="lazy"
          className="w-full h-full object-contain group-hover:scale-105 transition-transform duration-700"
          onError={(e) => { e.target.style.opacity = 0.25; }}
        />
      </div>
      <div className="pt-2 pb-1 px-1">
        <div className="text-[9px] tracking-[0.2em] uppercase text-[#0E4D2A]/60 font-sans truncate">
          {p.subcategory || p.category}
        </div>
        <div className="mt-0.5 font-serif text-[13px] md:text-sm leading-tight text-[#0E4D2A] line-clamp-2 min-h-[2.4em]">
          {p.title}
        </div>
        {p.price && (
          <div className="mt-1 font-serif text-sm font-bold text-[#0E4D2A]">{p.price}</div>
        )}
      </div>
    </div>
  </a>
);

export const FeaturedProductsStrip = () => {
  const { i18n } = useTranslation();
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);

  useEffect(() => {
    axios
      .get(`${API_URL}/api/amazon/products`, { params: { featured: true, limit: 12 } })
      .then((r) => setProducts(r.data || []))
      .catch(() => {});
  }, []);

  // Duplicate the list so the marquee loops seamlessly. If very few products,
  // triple them so there's always enough content on wide screens.
  const strip = useMemo(() => {
    if (!products.length) return [];
    if (products.length < 5) return [...products, ...products, ...products];
    return [...products, ...products];
  }, [products]);

  if (!products.length) return null;

  const copy = pickCopy(i18n.language);
  // Animation speed proportional to number of cards (slower on desktop)
  const durationSec = Math.max(28, strip.length * 4);

  return (
    <section
      className="relative w-full mt-14 md:mt-16"
      data-testid="featured-products-strip"
      aria-label="Featured products from the shop"
    >
      {/* Local keyframes for the marquee */}
      <style>{`
        @keyframes h0MarqueeScroll {
          from { transform: translateX(0); }
          to { transform: translateX(-50%); }
        }
        .h0-marquee-track {
          animation: h0MarqueeScroll var(--h0-marquee-duration, 40s) linear infinite;
          width: max-content;
        }
        .h0-marquee-track:hover { animation-play-state: paused; }
        @media (prefers-reduced-motion: reduce) {
          .h0-marquee-track { animation: none; }
        }
      `}</style>

      {/* Title */}
      <div className="text-center mb-6 md:mb-8 font-sans">
        <div className="text-[10px] md:text-[11px] tracking-[0.32em] uppercase text-[#D4AF37]">
          ⭐ {copy.title}
        </div>
        <div className="mt-1 h-px w-16 mx-auto bg-[#D4AF37]/40" />
      </div>

      {/* Marquee with fade edges */}
      <div className="relative overflow-hidden" style={{ maskImage: 'linear-gradient(to right, transparent 0%, black 6%, black 94%, transparent 100%)', WebkitMaskImage: 'linear-gradient(to right, transparent 0%, black 6%, black 94%, transparent 100%)' }}>
        <div
          className="h0-marquee-track flex gap-6 md:gap-8 items-start py-4"
          style={{ '--h0-marquee-duration': `${durationSec}s` }}
        >
          {strip.map((p, i) => (
            <Card
              key={`${p.id}-${i}`}
              p={p}
              tilt={ROTATIONS[i % ROTATIONS.length]}
            />
          ))}
        </div>
      </div>

      {/* CTA */}
      <div className="text-center mt-4">
        <button
          type="button"
          onClick={() => navigate('/botiga')}
          data-testid="featured-see-all"
          className="inline-block text-[11px] tracking-[0.25em] uppercase text-[#A8B4A6] hover:text-[#D4AF37] transition-colors font-sans"
        >
          {copy.cta}
        </button>
      </div>
    </section>
  );
};

export default FeaturedProductsStrip;
